import React, { useState, useEffect } from 'react';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functionsEurope } from '../../firebase';
import { notifySuccess, notifyError } from '../../utils/notify';
import {
  CircleStackIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

function MiniMetric({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-xl font-bold gch-text-primary">{value ?? '\u2014'}</p>
      <p className="text-[10px] gch-text-muted mt-0.5">{label}</p>
    </div>
  );
}

export default function AdminSystemHealth() {
  const [killSwitch, setKillSwitch] = useState(false);
  const [ksLoading, setKsLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Kill switch listener
  useEffect(() => {
    const ksRef = doc(db, 'system', 'killSwitch');
    const unsub = onSnapshot(ksRef, (snap) => {
      if (snap.exists()) setKillSwitch(snap.data().enabled || false);
      setKsLoading(false);
    }, () => setKsLoading(false));
    return unsub;
  }, []);

  // System health fetch
  useEffect(() => {
    (async () => {
      try {
        const res = await httpsCallable(functionsEurope, 'getSystemHealth')();
        setHealth(res.data);
      } catch (error) {
        console.error('Failed to load system health:', error);
      } finally {
        setHealthLoading(false);
      }
    })();
  }, []);

  const handleKillSwitchToggle = async () => {
    const newVal = !killSwitch;
    if (newVal && !window.confirm('Enable maintenance mode? This will show a maintenance page to all users.')) return;
    try {
      await setDoc(doc(db, 'system', 'killSwitch'), { enabled: newVal }, { merge: true });
      notifySuccess(`Maintenance mode ${newVal ? 'enabled' : 'disabled'}`);
    } catch (error) {
      notifyError('Failed to toggle maintenance mode');
    }
  };

  const handleRefreshCache = async () => {
    setRefreshing(true);
    try {
      await httpsCallable(functionsEurope, 'refreshAnalyticsCache')();
      // Re-fetch system health after refresh
      const res = await httpsCallable(functionsEurope, 'getSystemHealth')();
      setHealth(res.data);
      notifySuccess('Analytics cache refreshed');
    } catch (error) {
      notifyError('Failed to refresh cache');
    } finally {
      setRefreshing(false);
    }
  };

  const fmt = (v) => v != null ? v.toLocaleString() : '\u2014';

  const formatTimestamp = (ts) => {
    if (!ts) return '\u2014';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return isNaN(d.getTime()) ? '\u2014' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Kill Switch */}
      {ksLoading ? (
        <div className="gch-card h-24 animate-pulse bg-white/5 rounded-xl" />
      ) : (
        <div className="gch-card px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-wide gch-text-secondary mb-3">Maintenance Mode</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium gch-text-primary">Kill Switch</p>
              <p className="text-xs gch-text-muted">Show maintenance page to all users. Use in emergencies only.</p>
            </div>
            <button
              onClick={handleKillSwitchToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                killSwitch ? 'bg-red-500' : 'bg-white/20'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                killSwitch ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          {killSwitch && (
            <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-xs text-red-300 font-medium">Maintenance mode is ON. All users see a maintenance page.</p>
            </div>
          )}
        </div>
      )}

      {/* Database Stats */}
      {healthLoading ? (
        <div className="gch-card h-32 animate-pulse bg-white/5 rounded-xl" />
      ) : health ? (
        <div className="gch-card px-5 py-4">
          <div className="flex items-center gap-2 mb-4">
            <CircleStackIcon className="w-4 h-4 text-[var(--admin-accent)]" />
            <p className="text-xs font-semibold uppercase tracking-wide gch-text-secondary">Database</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MiniMetric label="Users" value={fmt(health.collections?.users)} />
            <MiniMetric label="Campaigns" value={fmt(health.collections?.campaign)} />
            <MiniMetric label="Workspaces" value={fmt(health.collections?.workspace)} />
            <MiniMetric label="Cleanup Logs" value={fmt(health.collections?.cleanupLogs)} />
          </div>
        </div>
      ) : null}

      {/* Storage & Cleanup */}
      {health?.storage && (
        <div className="gch-card px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-wide gch-text-secondary mb-3">Storage Cleanup</p>
          <div className="flex items-center gap-6">
            <div>
              <p className="text-sm font-medium gch-text-primary">{fmt(health.storage.totalFilesCleaned)}</p>
              <p className="text-[10px] gch-text-muted">Files cleaned</p>
            </div>
            <div>
              <p className="text-sm font-medium gch-text-primary">{formatTimestamp(health.storage.lastCleanup)}</p>
              <p className="text-[10px] gch-text-muted">Last cleanup</p>
            </div>
          </div>
        </div>
      )}

      {/* Refresh Cache */}
      <button
        onClick={handleRefreshCache}
        disabled={refreshing}
        className="w-full btn-admin-outline text-sm py-2.5 rounded-[var(--admin-card-radius)] flex items-center justify-center gap-2 disabled:opacity-50"
      >
        <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        {refreshing ? 'Refreshing analytics cache...' : 'Refresh Analytics Cache'}
      </button>
    </div>
  );
}
